import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

export function GroupButton({ label, dropdownLabel, onClick, to, children }: {
  label: ReactNode;
  dropdownLabel: string;
  onClick?: () => void;
  to?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const dropdownId = useId();

  useLayoutEffect(() => {
    if (!open) return;
    function position() {
      if (!root.current || !menu.current) return;
      const anchor = root.current.getBoundingClientRect();
      const viewport = document.documentElement;
      const margin = 8;
      const gap = 4;
      const below = Math.max(0, viewport.clientHeight - anchor.bottom - gap - margin);
      const above = Math.max(0, anchor.top - gap - margin);
      const height = menu.current.scrollHeight + 2;
      const placeAbove = height > below && above > below;
      menu.current.style.maxHeight = `${placeAbove ? above : below}px`;
      const rect = menu.current.getBoundingClientRect();
      menu.current.style.top = `${placeAbove ? anchor.top - gap - rect.height : anchor.bottom + gap}px`;
      menu.current.style.left = `${Math.max(margin, Math.min(anchor.right - rect.width, viewport.clientWidth - rect.width - margin))}px`;
    }
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, children]);

  useEffect(() => {
    if (!open) return;
    function dismiss(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node) && !menu.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  return (
    <div
      className="group-button"
      onBlur={event => {
        if (!root.current?.contains(event.relatedTarget) && !menu.current?.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={event => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          setOpen(false);
          toggle.current?.focus();
        }
      }}
      ref={root}
    >
      {to ? (
        <Link to={to} className="group-button-primary" onClick={() => setOpen(false)}>{label}</Link>
      ) : (
        <button type="button" className="group-button-primary" onClick={() => {
          setOpen(false);
          onClick?.();
        }}>{label}</button>
      )}
      <button
        type="button"
        className="group-button-toggle"
        ref={toggle}
        aria-label={dropdownLabel}
        aria-expanded={open}
        aria-controls={dropdownId}
        onClick={() => setOpen(value => !value)}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
          <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && createPortal(
        <div ref={menu} id={dropdownId} className="group-button-dropdown" onClick={event => {
          const button = (event.target as HTMLElement).closest("button");
          const link = (event.target as HTMLElement).closest("a");
          if (link || (button && !button.disabled)) setOpen(false);
        }}>
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
}
